import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Calendar, MapPin, Users, Building2, ArrowRight, Phone, Mail, MessageCircle, ChevronRight, Shield, ExternalLink, Eye, X, Camera } from "lucide-react";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

const API = `/api`;
const UPLOADS_URL = `/api/uploads`;

// Logo URLs
const LOGOS = {
  sag: `${UPLOADS_URL}/sag-logo.png`,
  win: `${UPLOADS_URL}/win-logo.png`,
  tdc: `${UPLOADS_URL}/tdc-logo.png`,
  edv: `${UPLOADS_URL}/edv-logo.png`,
};

// W.I.N. Summit Event Photos (fetched dynamically from API)
// No hardcoded fallback — managed from admin panel

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const staggerContainer = {
  animate: { transition: { staggerChildren: 0.1 } }
};

export default function PublicEventsPage() {
  const [events, setEvents] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [leadModalOpen, setLeadModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedPromotion, setSelectedPromotion] = useState(null);
  const [leadType, setLeadType] = useState("register_interest");
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", company: "", message: "" });
  const [galleryModalOpen, setGalleryModalOpen] = useState(false);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [fullImage, setFullImage] = useState(null);
  const [winHighlights, setWinHighlights] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [eventsRes, galleryRes, promotionsRes, winGalleryRes] = await Promise.all([
        axios.get(`${API}/events`),
        axios.get(`${API}/gallery`),
        axios.get(`${API}/promotions?is_active=true`),
        axios.get(`/api/win2026/gallery`)
      ]);
      setEvents(eventsRes.data);
      setGallery(galleryRes.data);
      setPromotions(promotionsRes.data);
      const winPhotos = (winGalleryRes.data || []).filter(i => i.type === "photo").map(i => ({ src: i.url, alt: i.caption }));
      setWinHighlights(winPhotos);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const upcomingEvents = events.filter(e => e.event_type === "upcoming" && e.status === "published");
  const popupEvents = events.filter(e => e.event_type === "popup" && e.status === "published");
  const pastEvents = events.filter(e => e.event_type === "past" && e.status === "published");

  const openLeadModal = (type, event = null, promotion = null) => {
    setLeadType(type);
    setSelectedEvent(event);
    setSelectedPromotion(promotion);
    setLeadModalOpen(true);
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/leads`, {
        ...formData,
        lead_type: leadType,
        event_id: selectedEvent?.id || null,
        promotion_id: selectedPromotion?.id || null,
        source: selectedPromotion ? "promotion" : "event"
      });
      toast.success("Thank you! We'll be in touch soon.");
      setLeadModalOpen(false);
      setFormData({ name: "", email: "", phone: "", company: "", message: "" });
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#00AEEF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-xl border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <a href="/" className="flex items-center gap-3" data-testid="nav-logo">
            <img 
              src={LOGOS.sag} 
              alt="Sebastian Alliance Group"
              className="h-14 w-auto"
            />
          </a>
          <div className="flex items-center gap-6">
            <a href="#upcoming" className="text-[#1E3A5F] hover:text-[#00AEEF] transition-colors text-sm font-medium hidden md:block">Events</a>
            <a href="#gallery" className="text-[#1E3A5F] hover:text-[#00AEEF] transition-colors text-sm font-medium hidden md:block">Gallery</a>
            <a href="#promotions" className="text-[#1E3A5F] hover:text-[#00AEEF] transition-colors text-sm font-medium hidden md:block">Promotions</a>
            <a href="/admin" className="text-[#1E3A5F] hover:text-[#00AEEF] transition-colors text-sm font-medium hidden md:block">Admin</a>
            <Button 
              onClick={() => openLeadModal("become_partner")} 
              className="bg-[#FFCC00] hover:bg-[#FFE066] text-[#1E3A5F] font-semibold px-6"
              data-testid="nav-partner-btn"
            >
              Become a Partner
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center bg-gradient-to-br from-[#1E3A5F] via-[#2B4A6F] to-[#1E3A5F] pt-20">
        <div className="absolute inset-0 overflow-hidden">
          <img 
            src="https://images.unsplash.com/photo-1768508949307-044ec3c1836a?w=1920" 
            alt="Corporate event" 
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1E3A5F] via-[#1E3A5F]/80 to-transparent" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <motion.div {...fadeInUp}>
            <span className="inline-block px-4 py-2 bg-[#00AEEF]/20 border border-[#00AEEF]/40 text-[#00AEEF] text-xs tracking-widest uppercase mb-8 rounded-full">
              Strategic Financial Partnerships
            </span>
          </motion.div>
          
          <motion.h1 
            className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Connecting Leaders.<br />
            <span className="text-[#FFCC00]">Building Futures.</span>
          </motion.h1>
          
          <motion.p 
            className="text-lg md:text-xl text-gray-300 max-w-2xl mx-auto mb-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            Sebastian Alliance Group brings together industry leaders, financial institutions, and strategic partners to shape the future of business in the Caribbean.
          </motion.p>
          
          <motion.div 
            className="flex flex-col sm:flex-row gap-4 justify-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <Button 
              onClick={() => openLeadModal("register_interest")} 
              className="bg-[#FFCC00] hover:bg-[#FFE066] text-[#1E3A5F] font-semibold px-8 py-6 text-lg rounded-md"
              data-testid="hero-register-btn"
            >
              Register for Events <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button 
              variant="outline" 
              className="border-white/40 text-white hover:border-[#00AEEF] hover:text-[#00AEEF] px-8 py-6 text-lg rounded-md"
              onClick={() => document.getElementById("gallery").scrollIntoView({ behavior: "smooth" })}
              data-testid="hero-gallery-btn"
            >
              View Past Events
            </Button>
          </motion.div>
        </div>
        
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2">
          <motion.div 
            animate={{ y: [0, 10, 0] }} 
            transition={{ duration: 2, repeat: Infinity }}
            className="w-6 h-10 border-2 border-white/40 rounded-full flex justify-center pt-2"
          >
            <div className="w-1.5 h-1.5 bg-[#FFCC00] rounded-full" />
          </motion.div>
        </div>
      </section>

      {/* Featured Event: W.I.N Summit 2026 */}
      <section className="py-16 bg-white relative overflow-hidden border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            {/* Left: Event Info */}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-4">
                <span className="px-3 py-1 bg-[#D4AF37] text-white text-xs font-bold rounded-full uppercase tracking-wider">
                  Featured Event
                </span>
                <span className="px-3 py-1 bg-[#008B9B] text-white text-xs font-bold rounded-full uppercase tracking-wider">
                  Free Event
                </span>
                <span className="text-gray-500 text-sm">April 9, 2026</span>
              </div>
              
              <h2 className="text-4xl md:text-5xl font-bold mb-2 text-[#1E3A5F]" style={{ fontFamily: "'Playfair Display', serif" }}>
                W.I.N Entrepreneurial<br />Summit 2026
              </h2>
              <p className="text-base text-[#D4AF37] font-semibold mb-4 italic">Where Ideas Network</p>
              
              <p className="text-lg text-gray-600 mb-6 max-w-xl">
                <span className="text-[#008B9B] font-semibold">From Idea to Activation.</span> Empowering Bahamian entrepreneurs with business licensing, banking, digital storefronts, and financial infrastructure — all in one summit.
              </p>
              
              <div className="flex items-center gap-6 mb-8">
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-500">Hosted by Tourism Development Corporation</span>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-4">
                <a href="/win2026" className="inline-flex items-center gap-2 px-6 py-3 bg-[#008B9B] hover:bg-[#006064] text-white font-semibold rounded-md transition-colors" data-testid="featured-register-btn">
                  Register Now <ArrowRight className="w-4 h-4" />
                </a>
                <a href="/win2026" className="inline-flex items-center gap-2 px-6 py-3 border border-[#008B9B] text-[#008B9B] hover:bg-[#008B9B] hover:text-white rounded-md transition-colors" data-testid="featured-learn-btn">
                  Learn More
                </a>
              </div>
            </div>
            
            {/* Right: Summit Logo */}
            <div className="flex-shrink-0">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
              >
                <img 
                  src={LOGOS.win}
                  alt="W.I.N Entrepreneurial Summit 2026" 
                  className="h-48 md:h-64 w-auto"
                />
              </motion.div>
            </div>
          </div>
          
          {/* Registration Type Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            <a href="/win2026" className="group bg-[#008B9B] rounded-xl p-6 hover:bg-[#006064] transition-all">
              <Users className="w-8 h-8 text-[#D4AF37] mb-4" />
              <h3 className="text-white font-semibold text-lg mb-2">Entrepreneurs</h3>
              <p className="text-white/70 text-sm mb-4">Register to access business activation services, funding, and digital infrastructure support.</p>
              <span className="text-[#D4AF37] text-sm font-medium group-hover:underline">Register as Entrepreneur →</span>
            </a>
            <a href="/win2026" className="group bg-[#008B9B] rounded-xl p-6 hover:bg-[#006064] transition-all">
              <Building2 className="w-8 h-8 text-[#D4AF37] mb-4" />
              <h3 className="text-white font-semibold text-lg mb-2">Exhibitors</h3>
              <p className="text-white/70 text-sm mb-4">Showcase your services and connect with Bahamian entrepreneurs at the summit.</p>
              <span className="text-[#D4AF37] text-sm font-medium group-hover:underline">Become an Exhibitor →</span>
            </a>
            <a href="/win2026" className="group bg-[#008B9B] rounded-xl p-6 hover:bg-[#006064] transition-all">
              <Building2 className="w-8 h-8 text-[#D4AF37] mb-4" />
              <h3 className="text-white font-semibold text-lg mb-2">Financial Institutions</h3>
              <p className="text-white/70 text-sm mb-4">Partner with the summit to provide banking, lending, and merchant services.</p>
              <span className="text-[#D4AF37] text-sm font-medium group-hover:underline">Register as Institution →</span>
            </a>
          </div>
        </div>
      </section>

      {/* W.I.N. Summit Photo Highlights */}
      {winHighlights.length > 0 && (
      <section className="py-16 bg-gradient-to-br from-[#008B9B] to-[#006064] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.3'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        <div className="max-w-7xl mx-auto px-6 relative">
          <motion.div 
            className="flex flex-col md:flex-row items-center justify-between mb-10 gap-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Camera className="w-5 h-5 text-[#D4AF37]" />
                <span className="text-[#D4AF37] text-sm font-bold uppercase tracking-widest">Summit Highlights</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                The W.I.N. Experience
              </h2>
            </div>
            <a 
              href="/win2026" 
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#D4AF37] hover:bg-[#B4941F] text-white font-semibold rounded-md transition-colors"
              data-testid="highlights-view-all"
            >
              View Full Gallery <ArrowRight className="w-4 h-4" />
            </a>
          </motion.div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {winHighlights.map((photo, index) => (
              <motion.div
                key={index}
                className={`relative overflow-hidden rounded-xl cursor-pointer group ${
                  index === 0 ? "md:col-span-2 md:row-span-2" : ""
                }`}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.06 }}
                onClick={() => setFullImage(photo.src)}
                data-testid={`win-highlight-${index}`}
              >
                <img 
                  src={photo.src} 
                  alt={photo.alt}
                  className={`w-full object-cover group-hover:scale-105 transition-transform duration-500 ${
                    index === 0 ? "h-48 md:h-[360px]" : "h-36 md:h-[174px]"
                  }`}
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300" />
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="w-9 h-9 bg-white/90 rounded-full flex items-center justify-center">
                    <Eye className="w-4 h-4 text-[#008B9B]" />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Upcoming & Pop-up Events */}
      <section id="upcoming" className="py-24 md:py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div 
            className="mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-[#00AEEF] text-xs tracking-widest uppercase mb-4 block font-semibold">What's Coming</span>
            <h2 className="text-3xl md:text-5xl font-semibold text-[#1E3A5F]">Upcoming Events</h2>
          </motion.div>

          {upcomingEvents.length === 0 && popupEvents.length === 0 ? (
            <motion.div 
              className="grid grid-cols-1 md:grid-cols-2 gap-6"
              variants={staggerContainer}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
            >
              {/* W.I.N Summit 2026 - Featured Upcoming Event */}
              <motion.div 
                variants={fadeInUp}
                className="group bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300 md:col-span-2"
                data-testid="event-card-win2026"
              >
                <div className="flex flex-col md:flex-row">
                  <div className="relative md:w-1/2 h-64 md:h-auto overflow-hidden bg-gradient-to-br from-[#008B9B] to-[#006064] flex items-center justify-center p-8">
                    <img 
                      src={LOGOS.win}
                      alt="W.I.N Entrepreneurial Summit 2026"
                      className="h-40 md:h-48 w-auto"
                    />
                    <span className="absolute top-4 left-4 px-3 py-1 bg-[#D4AF37] text-white text-xs font-bold uppercase rounded">
                      Featured Event
                    </span>
                    <span className="absolute top-4 right-4 px-3 py-1 bg-white text-[#008B9B] text-xs font-bold uppercase rounded">
                      Free Event
                    </span>
                  </div>
                  <div className="p-6 md:w-1/2 flex flex-col justify-center">
                    <div className="flex items-center gap-4 text-gray-500 text-sm mb-4">
                      <span className="flex items-center gap-1.5">
                        <Calendar className="w-4 h-4" />
                        April 9, 2026
                      </span>
                      <span className="flex items-center gap-1.5">
                        <MapPin className="w-4 h-4" />
                        The Bahamas
                      </span>
                    </div>
                    <h3 className="text-2xl font-semibold text-[#1E3A5F] mb-1 group-hover:text-[#008B9B] transition-colors">
                      W.I.N Entrepreneurial Summit 2026
                    </h3>
                    <p className="text-sm text-[#D4AF37] font-semibold italic mb-3">Where Ideas Network</p>
                    <p className="text-gray-600 text-sm mb-4">
                      From Idea to Activation. Empowering Bahamian entrepreneurs with business licensing, banking, digital storefronts, and financial infrastructure.
                    </p>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="h-8 px-3 bg-[#008B9B]/10 rounded flex items-center">
                        <span className="text-[#008B9B] font-bold text-sm">TDC</span>
                      </div>
                      <span className="text-xs text-gray-500">Hosted by Tourism Development Corporation</span>
                    </div>
                    <a 
                      href="/win2026"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-[#008B9B] hover:bg-[#006064] text-white font-semibold rounded-md transition-colors w-fit"
                    >
                      Register Now <ArrowRight className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          ) : (
            <motion.div 
              className="grid grid-cols-1 md:grid-cols-2 gap-6"
              variants={staggerContainer}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
            >
              {/* W.I.N Summit 2026 - Featured Upcoming Event */}
              <motion.div 
                variants={fadeInUp}
                className="group bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300 md:col-span-2"
                data-testid="event-card-win2026"
              >
                <div className="flex flex-col md:flex-row">
                  <div className="relative md:w-1/2 h-64 md:h-auto overflow-hidden bg-gradient-to-br from-[#008B9B] to-[#006064] flex items-center justify-center p-8">
                    <img 
                      src={LOGOS.win}
                      alt="W.I.N Entrepreneurial Summit 2026"
                      className="h-40 md:h-48 w-auto"
                    />
                    <span className="absolute top-4 left-4 px-3 py-1 bg-[#D4AF37] text-white text-xs font-bold uppercase rounded">
                      Featured Event
                    </span>
                    <span className="absolute top-4 right-4 px-3 py-1 bg-white text-[#008B9B] text-xs font-bold uppercase rounded">
                      Free Event
                    </span>
                  </div>
                  <div className="p-6 md:w-1/2 flex flex-col justify-center">
                    <div className="flex items-center gap-4 text-gray-500 text-sm mb-4">
                      <span className="flex items-center gap-1.5">
                        <Calendar className="w-4 h-4" />
                        April 9, 2026
                      </span>
                      <span className="flex items-center gap-1.5">
                        <MapPin className="w-4 h-4" />
                        The Bahamas
                      </span>
                    </div>
                    <h3 className="text-2xl font-semibold text-[#1E3A5F] mb-1 group-hover:text-[#008B9B] transition-colors">
                      W.I.N Entrepreneurial Summit 2026
                    </h3>
                    <p className="text-sm text-[#D4AF37] font-semibold italic mb-3">Where Ideas Network</p>
                    <p className="text-gray-600 text-sm mb-4">
                      From Idea to Activation. Empowering Bahamian entrepreneurs with business licensing, banking, digital storefronts, and financial infrastructure.
                    </p>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="h-8 px-3 bg-[#008B9B]/10 rounded flex items-center">
                        <span className="text-[#008B9B] font-bold text-sm">TDC</span>
                      </div>
                      <span className="text-xs text-gray-500">Hosted by Tourism Development Corporation</span>
                    </div>
                    <a 
                      href="/win2026"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-[#008B9B] hover:bg-[#006064] text-white font-semibold rounded-md transition-colors w-fit"
                    >
                      Register Now <ArrowRight className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </motion.div>

              {/* Other upcoming events */}
              {[...upcomingEvents, ...popupEvents].map((event, index) => (
                <motion.div 
                  key={event.id} 
                  variants={fadeInUp}
                  className="group bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl transition-shadow duration-300"
                  data-testid={`event-card-${index}`}
                >
                  <div className="relative h-56 overflow-hidden">
                    <img 
                      src={event.image_url || "https://images.unsplash.com/photo-1768508949307-044ec3c1836a?w=800"} 
                      alt={event.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1E3A5F]/80 to-transparent" />
                    {event.event_type === "popup" && (
                      <span className="absolute top-4 left-4 px-3 py-1 bg-[#00AEEF] text-white text-xs font-bold uppercase rounded">
                        Pop-up Event
                      </span>
                    )}
                    {event.is_featured && (
                      <span className="absolute top-4 right-4 px-3 py-1 bg-[#FFCC00] text-[#1E3A5F] text-xs font-bold uppercase rounded">
                        Featured
                      </span>
                    )}
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-4 text-gray-500 text-sm mb-4">
                      <span className="flex items-center gap-1.5">
                        <Calendar className="w-4 h-4" />
                        {formatDate(event.date_start)}
                        {event.date_end && ` - ${formatDate(event.date_end)}`}
                      </span>
                      {event.location && (
                        <span className="flex items-center gap-1.5">
                          <MapPin className="w-4 h-4" />
                          {event.location}
                        </span>
                      )}
                    </div>
                    <h3 className="text-xl font-semibold text-[#1E3A5F] mb-3 group-hover:text-[#00AEEF] transition-colors">
                      {event.title}
                    </h3>
                    <p className="text-gray-600 text-sm mb-6 line-clamp-2">{event.description}</p>
                    {event.partner_name && (
                      <div className="inline-flex items-center gap-2 px-3 py-1.5 mb-6 bg-[#00AEEF]/10 border border-[#00AEEF]/30 rounded-full">
                        <Building2 className="w-4 h-4 text-[#00AEEF]" />
                        <span className="text-[#00AEEF] text-sm font-medium">{event.partner_name}</span>
                      </div>
                    )}
                    <div className="flex gap-3">
                      <Button 
                        onClick={() => openLeadModal("register_interest", event)} 
                        className="flex-1 bg-[#00AEEF] hover:bg-[#0095CC] text-white rounded-md"
                        data-testid={`event-register-btn-${index}`}
                      >
                        {event.cta_text || "Register Interest"}
                      </Button>
                      {event.event_type === "popup" && (
                        <Button 
                          variant="outline" 
                          onClick={() => openLeadModal("become_partner", event)}
                          className="border-gray-300 text-[#1E3A5F] hover:border-[#00AEEF] hover:text-[#00AEEF] rounded-md"
                          data-testid={`event-partner-btn-${index}`}
                        >
                          Partner
                        </Button>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </section>

      {/* Past Events Gallery */}
      <section id="gallery" className="py-24 md:py-32 bg-[#1E3A5F]">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div 
            className="mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-[#00AEEF] text-xs tracking-widest uppercase mb-4 block font-semibold">Our Track Record</span>
            <h2 className="text-3xl md:text-5xl font-semibold text-white">Past Events & Partners</h2>
            <p className="text-gray-300 mt-4 max-w-2xl">
              A showcase of successful events and partnerships with leading financial institutions across the Caribbean.
            </p>
          </motion.div>

          {/* Past Events Cards */}
          {pastEvents.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              {pastEvents.map((event, index) => (
                <motion.div 
                  key={event.id}
                  className="bg-white/10 backdrop-blur rounded-lg overflow-hidden group cursor-pointer"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() => {
                    const imgUrl = event.image_url || "https://images.unsplash.com/photo-1762158007817-e149aab49270?w=800";
                    setFullImage(imgUrl);
                  }}
                  data-testid={`past-event-${index}`}
                >
                  <div className="relative h-40 overflow-hidden">
                    <img 
                      src={event.image_url || "https://images.unsplash.com/photo-1762158007817-e149aab49270?w=800"} 
                      alt={event.title}
                      className="w-full h-full object-cover opacity-70 group-hover:opacity-100 group-hover:scale-105 transition-all duration-300"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1E3A5F] to-transparent" />
                    <div className="absolute top-2 right-2 bg-white/20 backdrop-blur rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Eye className="w-4 h-4 text-white" />
                    </div>
                  </div>
                  <div className="p-5">
                    <div className="text-gray-400 text-xs mb-2">{formatDate(event.date_start)}</div>
                    <h4 className="text-white font-medium mb-2">{event.title}</h4>
                    {event.partner_name && (
                      <div className="flex items-center gap-2 text-[#00AEEF] text-sm">
                        <Building2 className="w-3 h-3" />
                        {event.partner_name}
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {/* Gallery Albums */}
          {gallery.length > 0 && (
            <>
              <h3 className="text-xl font-semibold text-white mb-6">Photo Gallery</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {gallery.map((album, index) => (
                  <motion.div 
                    key={album.id}
                    className="bg-white/10 backdrop-blur rounded-lg overflow-hidden cursor-pointer group"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    onClick={() => { setSelectedAlbum(album); setGalleryModalOpen(true); }}
                    data-testid={`gallery-album-${index}`}
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={album.cover_image || album.images?.[0] || "https://images.unsplash.com/photo-1762158007817-e149aab49270?w=800"} 
                        alt={album.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#1E3A5F]/90 via-transparent to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-4">
                        <h4 className="text-white font-medium">{album.title}</h4>
                        {album.partner_name && (
                          <p className="text-[#00AEEF] text-sm flex items-center gap-1 mt-1">
                            <Building2 className="w-3 h-3" /> {album.partner_name}
                          </p>
                        )}
                        <p className="text-gray-400 text-xs mt-2">{album.images?.length || 0} photos</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Partner Logos Strip */}
      <section className="py-16 bg-white border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-center text-gray-500 text-sm uppercase tracking-widest mb-10 font-semibold">Trusted Partners</p>
          <div className="flex flex-wrap justify-center items-center gap-12">
            <div className="flex items-center gap-3 text-[#1E3A5F]">
              <Building2 className="w-8 h-8" />
              <span className="font-semibold">Scotiabank</span>
            </div>
            <div className="flex items-center gap-3 text-[#1E3A5F]">
              <Building2 className="w-8 h-8" />
              <span className="font-semibold">RBC Royal Bank</span>
            </div>
            <div className="flex items-center gap-3 text-[#1E3A5F]">
              <Building2 className="w-8 h-8" />
              <span className="font-semibold">Bank of the Bahamas</span>
            </div>
          </div>
        </div>
      </section>

      {/* Promotions Section */}
      {promotions.length > 0 && (
        <section id="promotions" className="py-24 md:py-32 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6">
            <motion.div 
              className="mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-[#FFCC00] text-xs tracking-widest uppercase mb-4 block font-semibold">Partner Promotions</span>
              <h2 className="text-3xl md:text-5xl font-semibold text-[#1E3A5F]">Special Offers</h2>
              <p className="text-gray-600 mt-4 max-w-2xl">
                Exclusive promotions from our trusted third-party partners. By appointment only.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {promotions.map((promo, index) => (
                <motion.div 
                  key={promo.id}
                  className="bg-white rounded-lg overflow-hidden shadow-md group"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.15 }}
                  data-testid={`promotion-card-${index}`}
                >
                  <div className="md:flex">
                    <div className="relative md:w-2/5 h-56 md:h-auto overflow-hidden">
                      <img 
                        src={promo.image_url || "https://images.unsplash.com/photo-1743356174523-b04efcc66b46?w=800"} 
                        alt={promo.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                      <div className="absolute top-4 left-4 px-3 py-1 bg-[#FFCC00] text-[#1E3A5F] text-xs font-bold rounded">
                        Third-Party Promotion
                      </div>
                    </div>
                    <div className="p-6 md:w-3/5">
                      <div className="flex items-center gap-2 text-gray-500 text-sm mb-3">
                        <Calendar className="w-4 h-4" />
                        {formatDate(promo.date_start)} - {formatDate(promo.date_end)}
                      </div>
                      <h3 className="text-xl font-semibold text-[#1E3A5F] mb-2">{promo.title}</h3>
                      <div className="flex items-center gap-3 mb-3">
                        <img 
                          src={LOGOS.edv} 
                          alt="Endless Dream Vacation Club"
                          className="h-10 w-auto"
                        />
                        <span className="text-[#00AEEF] text-sm font-medium">{promo.partner_name}</span>
                      </div>
                      <p className="text-gray-600 text-sm mb-4 line-clamp-3">{promo.description}</p>
                      <div className="flex items-center gap-2 text-gray-400 text-xs mb-6">
                        <Shield className="w-4 h-4" />
                        <span className="italic">{promo.disclaimer}</span>
                      </div>
                      <Button 
                        onClick={() => openLeadModal("register_interest", null, promo)} 
                        className="w-full bg-[#FFCC00] hover:bg-[#FFE066] text-[#1E3A5F] font-semibold rounded-md"
                        data-testid={`promotion-cta-${index}`}
                      >
                        {promo.cta_text} <ChevronRight className="ml-2 w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="py-16 bg-[#1E3A5F] border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-6">
                <img 
                  src={LOGOS.sag} 
                  alt="Sebastian Alliance Group"
                  className="h-20 w-auto"
                />
              </div>
              <p className="text-gray-300 text-sm max-w-md mb-6">
                Connecting industry leaders and financial institutions to build strategic partnerships across the Caribbean and beyond.
              </p>
              <div className="flex gap-4">
                <Button 
                  onClick={() => openLeadModal("request_callback")} 
                  variant="outline" 
                  size="sm"
                  className="border-white/30 text-white hover:border-[#00AEEF] hover:text-[#00AEEF]"
                  data-testid="footer-callback-btn"
                >
                  <Phone className="w-4 h-4 mr-2" /> Request Callback
                </Button>
              </div>
            </div>
            <div>
              <h5 className="text-white font-semibold mb-4">Quick Links</h5>
              <ul className="space-y-3 text-gray-300 text-sm">
                <li><a href="#upcoming" className="hover:text-[#00AEEF] transition-colors">Upcoming Events</a></li>
                <li><a href="#gallery" className="hover:text-[#00AEEF] transition-colors">Past Events</a></li>
                <li><a href="#promotions" className="hover:text-[#00AEEF] transition-colors">Promotions</a></li>
                <li><a href="/admin" className="hover:text-[#00AEEF] transition-colors">Admin Portal</a></li>
              </ul>
            </div>
            <div>
              <h5 className="text-white font-semibold mb-4">Contact</h5>
              <ul className="space-y-3 text-gray-300 text-sm">
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-[#00AEEF]" />
                  events@sebasalliance.com
                </li>
                <li className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-[#00AEEF]" />
                  +1 (242) 698-1061
                </li>
                <li className="flex items-center gap-2">
                  <MessageCircle className="w-4 h-4 text-[#00AEEF]" />
                  WhatsApp Available
                </li>
              </ul>
            </div>
          </div>
          <div className="mt-12 pt-8 border-t border-white/10 text-center text-gray-400 text-sm">
            © {new Date().getFullYear()} Sebastian Alliance Group. All rights reserved.
          </div>
        </div>
      </footer>

      {/* Lead Capture Modal */}
      <Dialog open={leadModalOpen} onOpenChange={setLeadModalOpen}>
        <DialogContent className="bg-white border-gray-200 max-w-md" data-testid="lead-modal">
          <DialogHeader>
            <DialogTitle className="text-[#1E3A5F] text-xl">
              {leadType === "register_interest" && "Register Your Interest"}
              {leadType === "request_callback" && "Request a Callback"}
              {leadType === "become_partner" && "Become a Partner"}
            </DialogTitle>
            <DialogDescription className="text-gray-600">
              {selectedEvent && `For: ${selectedEvent.title}`}
              {selectedPromotion && `For: ${selectedPromotion.title}`}
              {!selectedEvent && !selectedPromotion && "We'll get back to you shortly."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleLeadSubmit} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="name" className="text-gray-700">Full Name *</Label>
              <Input 
                id="name" 
                value={formData.name} 
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                required
                className="bg-gray-50 border-gray-200 text-[#1E3A5F] mt-1"
                placeholder="John Doe"
                data-testid="lead-name-input"
              />
            </div>
            <div>
              <Label htmlFor="email" className="text-gray-700">Email Address *</Label>
              <Input 
                id="email" 
                type="email"
                value={formData.email} 
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
                className="bg-gray-50 border-gray-200 text-[#1E3A5F] mt-1"
                placeholder="john@company.com"
                data-testid="lead-email-input"
              />
            </div>
            <div>
              <Label htmlFor="phone" className="text-gray-700">Phone Number</Label>
              <Input 
                id="phone" 
                value={formData.phone} 
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                className="bg-gray-50 border-gray-200 text-[#1E3A5F] mt-1"
                placeholder="+1 (242) 555-0123"
                data-testid="lead-phone-input"
              />
            </div>
            {leadType === "become_partner" && (
              <div>
                <Label htmlFor="company" className="text-gray-700">Company Name</Label>
                <Input 
                  id="company" 
                  value={formData.company} 
                  onChange={(e) => setFormData({...formData, company: e.target.value})}
                  className="bg-gray-50 border-gray-200 text-[#1E3A5F] mt-1"
                  placeholder="Your Company Ltd."
                  data-testid="lead-company-input"
                />
              </div>
            )}
            <div>
              <Label htmlFor="message" className="text-gray-700">Message (Optional)</Label>
              <Textarea 
                id="message" 
                value={formData.message} 
                onChange={(e) => setFormData({...formData, message: e.target.value})}
                className="bg-gray-50 border-gray-200 text-[#1E3A5F] mt-1 min-h-[80px]"
                placeholder="Any specific questions or requirements..."
                data-testid="lead-message-input"
              />
            </div>
            <Button type="submit" className="w-full bg-[#00AEEF] hover:bg-[#0095CC] text-white" data-testid="lead-submit-btn">
              Submit
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Gallery Modal */}
      <Dialog open={galleryModalOpen} onOpenChange={(open) => { setGalleryModalOpen(open); if (!open) setFullImage(null); }}>
        <DialogContent className="bg-white border-gray-200 max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="gallery-modal">
          <DialogHeader>
            <DialogTitle className="text-[#1E3A5F] text-xl">{selectedAlbum?.title}</DialogTitle>
            <DialogDescription className="text-gray-600">
              {selectedAlbum?.description}
              {selectedAlbum?.partner_name && ` | Partner: ${selectedAlbum.partner_name}`}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
            {selectedAlbum?.images?.map((img, idx) => (
              <img 
                key={idx} 
                src={img} 
                alt={`${selectedAlbum.title} - ${idx + 1}`}
                className="w-full h-48 object-cover rounded-lg cursor-pointer hover:opacity-80 transition-opacity hover:ring-2 hover:ring-[#008B9B]"
                onClick={() => { setGalleryModalOpen(false); setTimeout(() => setFullImage(img), 200); }}
              />
            ))}
          </div>
        </DialogContent>
      </Dialog>
      {/* Full Image Lightbox */}
      {fullImage && (
        <div 
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4 cursor-pointer"
          onClick={() => setFullImage(null)}
          data-testid="image-lightbox"
        >
          <button 
            className="absolute top-6 right-6 text-white bg-white/20 hover:bg-white/40 rounded-full p-2 transition-colors"
            onClick={() => setFullImage(null)}
          >
            <X className="w-6 h-6" />
          </button>
          <img 
            src={fullImage} 
            alt="Full size view"
            className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
